'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();

import {
  cn,
  formatNavBadgeCount,
  getSubmenuBadgeLeftClass,
  isSubmenuItemActive,
  submenuMatchesPathname,
} from '@workspace/ui/lib';
import type { MenuItem } from '@workspace/ui/types';
import { ChevronDown } from 'lucide-react';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  SidebarMenuButton,
  SidebarMenuLabel,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../index';

export function CustomSidebarMenuItem({ item }: { item: MenuItem }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const search = searchParams.toString();
  const hasSubItems = Boolean(item.items?.length);
  const isParentActive = useMemo(
    () =>
      hasSubItems
        ? item.items!.some((sub) => submenuMatchesPathname(pathname, sub, search))
        : false,
    [hasSubItems, item.items, pathname, search]
  );
  const [isExpanded, setIsExpanded] = useState(isParentActive);
  const toggleExpanded = useCallback(() => setIsExpanded((prev) => !prev), []);
  /** Last committed `isParentActive`; updated in layout effect so render can detect true→false on pathname change before paint. */
  const committedParentActiveRef = useRef<boolean | undefined>(undefined);
  const leftActiveSubtree =
    hasSubItems && committedParentActiveRef.current === true && !isParentActive;
  const displayExpanded =
    hasSubItems && leftActiveSubtree ? false : hasSubItems ? isExpanded : false;

  useLayoutEffect(() => {
    if (!hasSubItems) return;
    const prev = committedParentActiveRef.current;
    if (prev === undefined) {
      committedParentActiveRef.current = isParentActive;
      return;
    }
    if (isParentActive && !prev) setIsExpanded(true);
    if (!isParentActive && prev) setIsExpanded(false);
    committedParentActiveRef.current = isParentActive;
  }, [hasSubItems, isParentActive]);

  if (item.component) {
    return <SidebarMenuButton asChild>{item.component}</SidebarMenuButton>;
  }

  // Check if pathname matches any excluded prefix
  const isExcluded = item.excludeUrlPrefix
    ? Array.isArray(item.excludeUrlPrefix)
      ? item.excludeUrlPrefix.some((prefix) => pathname.startsWith(prefix))
      : pathname.startsWith(item.excludeUrlPrefix)
    : false;

  // Determine if menu item is active:
  // - If urlPrefix is set: check if pathname starts with it (and doesn't match any excludeUrlPrefix)
  // - Otherwise: check exact match with url
  const isActive = item.urlPrefix
    ? pathname.startsWith(item.urlPrefix) && !isExcluded
    : pathname === item.url;

  // Item with sub items: render parent label + SidebarMenuSub (shown when expanded, or when collapsed but a sub-route is active — then only the active sub)
  if (hasSubItems) {
    const visibleSubItems = displayExpanded
      ? item.items!
      : isParentActive
        ? item.items!.filter(
            (sub) => !sub.inactive && isSubmenuItemActive(pathname, sub.url, item.items!, search)
          )
        : item.items!;

    return (
      <>
        <SidebarMenuButton
          asChild
          variant={item.disabled ? 'disabled' : 'default'}
          isActive={displayExpanded || isParentActive}
          disabled={item.disabled}
          className={cn(
            'active:bg-transparent! data-[active=true]:bg-transparent! overflow-visible',
            item.disabled && 'opacity-50'
          )}
          onClick={toggleExpanded}
        >
          <button
            type="button"
            className="flex w-full cursor-default items-center justify-between overflow-visible"
          >
            <span className="flex min-w-0 flex-1 items-center gap-2">
              <span className="relative flex size-5 shrink-0 items-center justify-center [&>svg]:size-5">
                {typeof item.badge === 'number' &&
                item.badge > 0 &&
                !displayExpanded &&
                !isParentActive ? (
                  <span className="absolute -right-3 top-1 flex h-4 min-w-4 -translate-x-2 -translate-y-1/2 items-center justify-center rounded-full bg-red-500 px-1 text-[8px] p-1 leading-none tabular-nums text-white">
                    {formatNavBadgeCount(item.badge)}
                  </span>
                ) : null}
                {item.icon}
              </span>
              <SidebarMenuLabel className="min-w-0 flex-1">{item.title}</SidebarMenuLabel>
            </span>
            <ChevronDown
              className={cn(
                'size-4! shrink-0 text-neutral-200 transition-transform',
                displayExpanded && 'rotate-180'
              )}
            />
          </button>
        </SidebarMenuButton>
        {(displayExpanded || isParentActive) && visibleSubItems.length > 0 && (
          <SidebarMenuSub className="ml-4 mr-0 mt-2 gap-2 overflow-visible border-l border-grey-200 p-0">
            {visibleSubItems.map((sub) => {
              const isSubActive =
                !sub.inactive && isSubmenuItemActive(pathname, sub.url, item.items!, search);
              const isInactive = Boolean(sub.inactive);

              return (
                <SidebarMenuSubItem
                  key={sub.url}
                  className={cn(
                    '-ml-px overflow-visible border-l border-grey-200 pl-0',
                    !isInactive && 'hover:border-pink-500',
                    isSubActive && 'bg-pink-50 dark:bg-grey-900 border-pink-500'
                  )}
                >
                  {isInactive ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <SidebarMenuSubButton
                          asChild
                          className="cursor-default text-grey-600 hover:bg-transparent dark:hover:bg-transparent overflow-visible p-4 pr-2!"
                        >
                          <span className="flex w-full items-center gap-2">
                            <span className="w-2.5 shrink-0" />
                            {sub.icon}
                            <span className="min-w-0 flex-1 truncate">{sub.title}</span>
                          </span>
                        </SidebarMenuSubButton>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" align="start">
                        {sub.tooltip ?? ''}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <div className="relative ">
                      {/* Badge centered on border (pl-0 on parent so content starts at border) */}
                      {typeof sub.badge === 'number' && sub.badge > 0 ? (
                        <span
                          className={cn(
                            'absolute top-1/2 -translate-x-1/2 flex h-4 min-w-4 -translate-y-1/2 items-center justify-center rounded-full bg-red-500 px-1 text-[8px] p-1 leading-none tabular-nums text-white',
                            getSubmenuBadgeLeftClass(sub.badge)
                          )}
                        >
                          {formatNavBadgeCount(sub.badge)}
                        </span>
                      ) : (
                        <span className="w-2.5 shrink-0" />
                      )}
                      <SidebarMenuSubButton asChild className="overflow-visible p-4 pr-2!">
                        <Link href={sub.url} className="flex w-full items-center gap-2">
                          {sub.icon}
                          <SidebarMenuLabel className="min-w-0 flex-1">
                            {sub.title}
                          </SidebarMenuLabel>
                        </Link>
                      </SidebarMenuSubButton>
                    </div>
                  )}
                </SidebarMenuSubItem>
              );
            })}
          </SidebarMenuSub>
        )}
      </>
    );
  }

  return (
    <SidebarMenuButton asChild variant={item.disabled ? 'disabled' : 'default'} isActive={isActive}>
      <Link
        href={item.disabled ? '' : item.url || ''}
        className="flex items-center gap-2"
        {...(item.openInNewTab ? { target: '_blank' as const, rel: 'noopener noreferrer' } : {})}
      >
        <span className="relative flex size-5 shrink-0 items-center justify-center [&>svg]:size-5">
          <span className={cn(isActive && 'text-pink-500!')}>{item.icon}</span>
          {typeof item.badge === 'number' && item.badge > 0 && (
            <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[8px] p-1 leading-none tabular-nums text-white">
              {formatNavBadgeCount(item.badge)}
            </span>
          )}
        </span>
        <SidebarMenuLabel className="min-w-0 flex-1">{item.title}</SidebarMenuLabel>
      </Link>
    </SidebarMenuButton>
  );
}
